import useWindowSize from '@propertypal/shared/src/hooks/useWindowSize';
import { roundOff } from '@propertypal/shared/src/utils/number';
import useTouchDevice from '@propertypal/web-app/src/hooks/useTouchDevice';
import React, { useRef, useState, useMemo, useEffect, FunctionComponent } from 'react';
import { Container, Wrapper } from './PanZoomImage.style';

const MAX_ZOOM = 4;
const MIN_ZOOM = 1;

type Position = {
  oldX: number;
  oldY: number;
  x: number;
  y: number;
  z: number;
};

export const DEFAULT_ZOOM_POSITION: Position = {
  oldX: 0,
  oldY: 0,
  x: 0,
  y: 0,
  z: MIN_ZOOM,
};

export interface UsePanZoomImageData {
  position: Position;
  setPosition: React.Dispatch<React.SetStateAction<Position>>;
  zoomIn: () => void;
  zoomOut: () => void;
  zoomPercentage: number;
  touchDistanceRef: React.MutableRefObject<number | null>;
  positionRef: React.MutableRefObject<Omit<Position, 'oldX' | 'oldY'> | null>;
}

export const usePanZoomImage = (): UsePanZoomImageData => {
  const [position, setPosition] = useState<Position>(DEFAULT_ZOOM_POSITION);

  const touchDistanceRef = useRef<number | null>(null);
  const positionRef = useRef<Omit<Position, 'oldX' | 'oldY'> | null>(null);

  const zoomIn = () => {
    setPosition((prevPosition) => {
      // Increment zoom by 0.1 and round to one decimal place
      let newZoom = Math.round((prevPosition.z + (MAX_ZOOM - 1) / 10) * 10) / 10;
      if (newZoom > MAX_ZOOM) newZoom = MAX_ZOOM; // Prevent zooming out above 100%
      return {
        ...prevPosition,
        z: newZoom,
      };
    });
  };

  const zoomOut = () => {
    setPosition((prevPosition) => {
      // Decrement by 0.1 and round
      let newZoom = Math.round((prevPosition.z - (MAX_ZOOM - 1) / 10) * 10) / 10;
      if (newZoom < MIN_ZOOM) newZoom = MIN_ZOOM; // Prevent zooming out below 0%
      let x = prevPosition.x;
      let y = prevPosition.y;
      // if reaching min zoom, center image
      if (newZoom === MIN_ZOOM) {
        x = 0;
        y = 0;
      }
      return {
        ...prevPosition,
        x,
        y,
        z: newZoom,
      };
    });
  };

  const zoomPercentage = useMemo(() => Math.round((position.z - 1) * (100 / (MAX_ZOOM - 1))), [position.z]);

  return {
    position,
    setPosition,
    zoomIn,
    zoomOut,
    zoomPercentage,
    touchDistanceRef,
    positionRef,
  };
};

type PanZoomImageProps = React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> & {
  position: UsePanZoomImageData['position'];
  setPosition: UsePanZoomImageData['setPosition'];
  touchDistanceRef: UsePanZoomImageData['touchDistanceRef'];
  positionRef: UsePanZoomImageData['positionRef'];
};

const PanZoomImage: FunctionComponent<PanZoomImageProps> = ({
  position,
  setPosition,
  touchDistanceRef,
  positionRef,
  ...props
}) => {
  const [isPanning, setPanning] = useState<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const touchDevice = useTouchDevice();

  const windowSize = useWindowSize();

  // Calculate distance between two fingers
  const calculateDistance = (e: React.TouchEvent<HTMLDivElement>): number => {
    return Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
  };

  const onTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2 && !touchDistanceRef?.current) {
      const initialDistance = calculateDistance(e);
      touchDistanceRef.current = initialDistance;
    } else if (e.touches.length === 1) {
      setPanning(true);
      setPosition((prev) => ({
        ...prev,
        oldX: e.touches[0].clientX,
        oldY: e.touches[0].clientY,
      }));
    }
  };

  const onTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (e.touches.length === 2 && touchDistanceRef.current != null) {
      const newDistance = calculateDistance(e);
      const scaleChange = newDistance / touchDistanceRef.current;

      const newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, (positionRef?.current?.z || 1) * scaleChange));

      if (newZoom !== positionRef?.current?.z) {
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect && wrapperRef.current) {
          const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.x;
          const centerY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.y;

          // Calculate the difference between the image center and the pinch center
          const initialCenterX = (positionRef?.current?.x || 0) + rect.width / 2;
          const initialCenterY = (positionRef?.current?.y || 0) + rect.height / 2;
          const deltaX = centerX - initialCenterX;
          const deltaY = centerY - initialCenterY;

          // Calculate the new position with smoother transitions
          const newX = (positionRef?.current?.x || 0) + deltaX * (scaleChange - 1);
          const newY = (positionRef?.current?.y || 0) + deltaY * (scaleChange - 1);

          // Update the position in the ref (no re-render here)
          positionRef.current = {
            x: newZoom === MIN_ZOOM ? 0 : newX,
            y: newZoom === MIN_ZOOM ? 0 : newY,
            z: newZoom,
          };

          wrapperRef.current.style.transform = `translate(${newX}px, ${newY}px) scale(${newZoom})`;
        }
      }

      touchDistanceRef.current = newDistance;
    }
  };

  const onTouchEnd = () => {
    // Update the state with the final position on touch end
    if (!isPanning) {
      setPosition((prev) => ({ ...prev, ...positionRef.current }));
      touchDistanceRef.current = null;
    }
  };

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault();
    setPanning(true);

    setPosition((prev) => ({
      ...prev,
      oldX: e.clientX,
      oldY: e.clientY,
    }));
  };

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.deltaY && containerRef.current && imageRef?.current) {
      const sign = Math.sign(e.deltaY) / 10;
      const scale = 1 - sign;
      const rect = containerRef.current.getBoundingClientRect();

      const z = position.z * scale;
      const x = position.x * scale - (rect.width / 2 - e.clientX + rect.x) * sign;
      const y =
        position.y * scale -
        ((imageRef.current.naturalHeight * rect.width) / imageRef.current.naturalWidth / 2 - e.clientY + rect.y) * sign;

      if (z < MIN_ZOOM || z > MAX_ZOOM) {
        setPosition({
          ...position,
          x: z < MIN_ZOOM ? 0 : position.x,
          y: z < MIN_ZOOM ? 0 : position.y,
          z: z < MIN_ZOOM ? MIN_ZOOM : MAX_ZOOM,
        });
      } else
        setPosition({
          ...position,
          x,
          y,
          z,
        });
    }
  };

  const onMouseMove = (event: MouseEvent) => {
    if (isPanning && !touchDevice) {
      const imageBounds = imageRef?.current?.getBoundingClientRect();
      let x = position.x + event.clientX - position.oldX;
      let y = position.y + event.clientY - position.oldY;

      // prevent dragging image of screen horizontally
      if (imageBounds?.x && windowSize.width) {
        if (imageBounds.x < -(imageBounds.width - 110)) x -= imageBounds.x + imageBounds.width - 110;
        else if (imageBounds.x > windowSize.width - 110) x += windowSize.width - 110 - imageBounds.x;
      }

      // prevent dragging image of screen vertically
      if (windowSize.height && imageBounds) {
        if (imageBounds.y != null && windowSize.height < imageBounds.y + 20)
          y -= imageBounds.y - windowSize.height + 20;
        else if (imageBounds.bottom != null && imageBounds.bottom < 80) y += 80 - imageBounds.bottom;
      }

      setPosition({
        ...position,
        x,
        y,
        oldX: event.clientX,
        oldY: event.clientY,
      });
    }
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (isPanning && event.touches.length === 1 && position.z !== 1) {
      const touch = event.touches[0];

      // Get the current touch position relative to the container
      const touchX = touch.clientX;
      const touchY = touch.clientY;

      const imageBounds = imageRef?.current?.getBoundingClientRect();
      let x = position.x + touchX - position.oldX;
      let y = position.y + touchY - position.oldY;

      // prevent dragging image of screen horizontally
      if (imageBounds?.x && windowSize.width) {
        if (imageBounds.x < -(imageBounds.width - 10)) x -= imageBounds.x + imageBounds.width - 10;
        else if (imageBounds.x > windowSize.width - 10) x += windowSize.width - 10 - imageBounds.x;
      }

      // prevent dragging image of screen vertically
      if (windowSize.height && imageBounds) {
        if (imageBounds.y != null && windowSize.height < imageBounds.y + 80)
          y -= imageBounds.y - windowSize.height + 80;
        else if (imageBounds.bottom != null && imageBounds.bottom < 80) y += 80 - imageBounds.bottom;
      }

      setPosition((prev) => ({
        ...prev,
        x,
        y,
        oldX: touchX,
        oldY: touchY,
      }));
    }
  };

  useEffect(() => {
    const onMouseUp = () => setPanning(false);

    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', onMouseUp);

    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', onMouseUp);
    };
  }, [onMouseMove, handleTouchMove]);

  return (
    <>
      <Container
        ref={containerRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        data-testid="zoom-container"
      >
        <Wrapper
          z={position.z}
          style={{
            transform: `matrix(${roundOff(position.z, 2)}, 0, 0, ${roundOff(position.z, 2)}, ${position.x}, ${
              position.y
            })`,
          }}
          data-testid="zoom-wrapper"
          ref={wrapperRef}
        >
          <img data-testid="zoom-img" {...props} alt={props.alt} ref={imageRef} />
        </Wrapper>
      </Container>
    </>
  );
};

export default PanZoomImage;
